/* SAR services not available in production api-services build
import type { ExternalTableSchema } from '@packages/table-builder';

export const sarAwardsTable: ExternalTableSchema = {
  metadata: {
    title: 'SAR Awards',
    entity: 'SarAward',
    description: 'Stock appreciation rights awards — view, publish, export, and manage SAR grants.',
  },

  dataSource: {
    endpoint: '/api/award/api/app/sar-award/sar-award-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },

    filters: [
      {
        name: 'CreatedFromDate',
        placeholder: 'Created Date From',
        type: 'date',
      },
      {
        name: 'CreatedToDate',
        placeholder: 'Created Date To',
        type: 'date',
      },
      {
        name: 'ApprovalStatusIds',
        placeholder: 'Approval Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/sar-award/sar-award-statuses',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'SarAcceptance',
        placeholder: 'Accept Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award/api/app/sar-award/sar-acceptances',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'GrantFromDate',
        placeholder: 'Grant Date From',
        type: 'date',
      },
      {
        name: 'GrantToDate',
        placeholder: 'Grant Date To',
        type: 'date',
      },
    ],
  },

  columns: [
    {
      field: 'grantNo',
      header: 'Award #',
      type: 'text',
      sortable: true,
      width: 120,
    },
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      width: 180,
      couple: {
        titleField: 'participantName',
        subtitleField: 'employeeId',
      },
    },
    {
      field: 'issueDate',
      header: 'Grant Date',
      type: 'date',
      sortable: true,
      width: 120,
      align: 'center',
    },
    {
      field: 'awardType',
      header: 'Award Type',
      type: 'text',
      sortable: true,
      width: 100,
      align: 'center',
    },
    {
      field: 'awardStatus',
      header: 'Award Status',
      type: 'badge',
      sortable: false,
      width: 100,
      align: 'center',
      badge: {
        statusMap: {
          'Fully Vested': 'success',
          Expired: 'error',
          Rejected: 'error',
          Cancelled: 'error',
          Outstanding: 'info',
          Pending: 'info',
        },
        defaultVariant: 'default',
      },
    },
    {
      field: 'grantName',
      header: 'Grant Name',
      type: 'text',
      sortable: true,
      width: 150,
      visibility: 'picker',
    },
    {
      field: 'planName',
      header: 'Plan Name',
      type: 'text',
      sortable: false,
      width: 140,
      visibility: 'picker',
    },
    {
      field: 'basePrice',
      header: 'Base Price',
      type: 'currency',
      sortable: false,
      width: 110,
      align: 'center',
    },
    {
      field: 'exercisableUnits',
      header: 'Exercisable Units',
      type: 'number',
      sortable: false,
      width: 110,
      align: 'center',
    },
    {
      field: 'unitsGranted',
      header: 'Awarded Units',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'vestedUnits',
      header: 'Vested Units',
      type: 'number',
      sortable: false,
      width: 100,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'exercisedUnit',
      header: 'Exercised Units',
      type: 'number',
      sortable: true,
      width: 100,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'outStandingUnits',
      header: 'Outstanding Units',
      type: 'number',
      sortable: true,
      width: 110,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'unvestedUnit',
      header: 'Unvested Units',
      type: 'number',
      sortable: false,
      width: 100,
      align: 'center',
    },
    {
      field: 'expiryDate',
      header: 'Expiration Date',
      type: 'date',
      sortable: false,
      width: 120,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'vestingSchedule',
      header: 'Vesting Schedule',
      type: 'text',
      sortable: true,
      width: 140,
      visibility: 'picker',
    },
    {
      field: 'nextVestingDate',
      header: 'Next Vesting Date',
      type: 'date',
      sortable: false,
      width: 120,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'isSARAwardReleased',
      header: 'Publish Status',
      type: 'badge',
      sortable: true,
      width: 100,
      align: 'center',
      badge: {
        statusMap: { true: 'success', false: 'info' },
        defaultVariant: 'default',
      },
      conditionalValues: { true: 'PUBLISHED', false: 'NOT PUBLISHED' },
    },
    {
      field: 'status',
      header: 'Approval Status',
      type: 'badge',
      sortable: true,
      width: 120,
      align: 'center',
      badge: {
        statusMap: {
          Pending: 'info',
          Approved: 'success',
          Rejected: 'error',
          Cancelled: 'error',
          Expired: 'error',
        },
        defaultVariant: 'default',
      },
    },
    {
      field: 'isAccepted',
      header: 'Accept Status',
      type: 'badge',
      sortable: true,
      width: 100,
      align: 'center',
      visibility: 'picker',
      badge: {
        statusMap: { true: 'success', false: 'error' },
        defaultVariant: 'default',
      },
      conditionalValues: { true: 'ACCEPTED', false: 'NEEDS ACCEPTANCE' },
    },
    {
      field: 'awardReleasedDate',
      header: 'Publish Date',
      type: 'date',
      sortable: true,
      width: 120,
      align: 'center',
      visibility: 'picker',
    },
    {
      field: 'poolName',
      header: 'Pool',
      type: 'text',
      sortable: true,
      width: 120,
      visibility: 'picker',
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_SAR_AWARD', label: 'View' },
      { id: 'EDIT_SAR_AWARD', label: 'Edit' },
      { id: 'DELETE_SAR_AWARD', label: 'Delete', variant: 'danger' },
      { id: 'CANCEL_SAR_AWARD', label: 'Cancel', variant: 'danger' },
      { id: 'PUBLISH_SAR_AWARD', label: 'Publish Award' },
      { id: 'VIEW_COMMENTS_SAR_AWARD', label: 'View Comments' },
      { id: 'REJECT_SAR_AWARD', label: 'Reject', variant: 'danger' },
    ],
    bulk: [
      { id: 'BULK_PUBLISH_SAR_AWARD', label: 'Publish Award' },
      { id: 'BULK_DELETE_SAR_AWARD', label: 'Bulk Delete', variant: 'danger' },
      { id: 'BULK_EXPORT_SAR_AWARD', label: 'Bulk Export XLS' },
    ],
    toolbar: {
      primary: { id: 'CREATE_SAR_AWARD', label: 'Grant New SAR', icon: 'add_circle_outline' },
      secondary: [
        {
          label: 'Download Template',
          icon: 'download',
          type: 'download' as const,
          children: [{ id: 'DOWNLOAD_TEMPLATE_SAR_AWARD', label: 'Download Template' }],
        },
        {
          label: 'Import Bulk XLS',
          icon: 'upload',
          type: 'upload' as const,
          children: [{ id: 'IMPORT_BULK_SAR_AWARD', label: 'Import Bulk XLS' }],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'pagination', defaultSize: 50 },
    selection: { keyField: 'id', selectAllPages: true },
    columnPreferences: { persistenceKey: 'SarAwards', resizable: true, reorderable: true },
    defaultSort: { field: 'issueDate', direction: 'desc' },
    filterGrid: { columns: 3 },
    import: { enabled: true, description: 'Quick & easy to import large volumes of data in an instant.' },
  },
};

*/
